import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Types ───────────────────────────────────────────────
interface Cliente {
  id: string;
  razon_social: string;
  nombre_comercial: string | null;
}

interface TaxSummary {
  id: string;
  cliente_id: string;
  periodo: string;
  ejercicio_fiscal: number;
  iva_repercutido: number;
  iva_soportado: number;
  iva_resultado: number;
  base_imponible_emitidas: number;
  base_imponible_recibidas: number;
  retenciones_practicadas: number;
  retenciones_soportadas: number;
  total_suplidos: number;
  num_facturas_emitidas: number;
  num_facturas_recibidas: number;
  last_sync_at: string | null;
}

interface IvaDetail {
  id: string;
  tax_summary_id: string;
  tipo_factura: 'emitida' | 'recibida';
  porcentaje_iva: number;
  base_imponible: number;
  cuota_iva: number;
  num_facturas: number;
}

type PeriodFilter = 'mes' | 'trimestre' | 'year2025' | 'year2026';

interface Anomaly {
  type: 'warning' | 'danger';
  icon: string;
  message: string;
}

// ─── Constants ───────────────────────────────────────────
const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const SHORT_MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'mes', label: 'Mes actual' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'year2025', label: 'Año 2025' },
  { value: 'year2026', label: 'Año 2026' },
];

// ─── Helpers ─────────────────────────────────────────────
function formatEur(value: number): string {
  const formatted = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return `€${formatted}`;
}

function formatPct(value: number | null): string {
  if (value === null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getYearMonths(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
}

function getCurrentQuarterMonths(): string[] {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const prevQ = quarter === 0 ? 3 : quarter - 1;
  const prevYear = quarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return Array.from({ length: 3 }, (_, i) => `${prevYear}-${String(prevQ * 3 + i + 1).padStart(2, '0')}`);
}

function getPeriodsForFilter(filter: PeriodFilter): string[] {
  switch (filter) {
    case 'mes': return [getCurrentPeriod()];
    case 'trimestre': return getCurrentQuarterMonths();
    case 'year2025': return getYearMonths(2025);
    case 'year2026': return getYearMonths(2026);
  }
}

function getPreviousPeriods(filter: PeriodFilter): string[] {
  const now = new Date();
  switch (filter) {
    case 'mes': {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1);
      return [`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`];
    }
    case 'trimestre': {
      const quarter = Math.floor(now.getMonth() / 3);
      const prevQ = quarter === 0 ? 3 : quarter - 1;
      const prevYear = quarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return Array.from({ length: 3 }, (_, i) => `${prevYear}-${String(prevQ * 3 + i + 1).padStart(2, '0')}`);
    }
    case 'year2025': return getYearMonths(2024);
    case 'year2026': return getYearMonths(2025);
  }
}

function getPeriodLabel(filter: PeriodFilter): string {
  const now = new Date();
  switch (filter) {
    case 'mes': return `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
    case 'trimestre': {
      const q = Math.floor(now.getMonth() / 3) + 1;
      return `Trimestre ${q} — ${now.getFullYear()}`;
    }
    case 'year2025': return 'Ejercicio fiscal 2025';
    case 'year2026': return 'Ejercicio fiscal 2026';
  }
}

function getComparisonLabel(filter: PeriodFilter): string {
  switch (filter) {
    case 'mes': return 'vs. mes anterior';
    case 'trimestre': return 'vs. trimestre anterior';
    case 'year2025': return 'vs. año 2024';
    case 'year2026': return 'vs. año 2025';
  }
}

function formatMonth(periodo: string): string {
  const [year, month] = periodo.split('-');
  return `${SHORT_MONTHS[parseInt(month, 10) - 1]} ${year}`;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'ahora mismo';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHrs < 24) return `hace ${diffHrs}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function num(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v) || 0;
  return 0;
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

// ─── Main Component ──────────────────────────────────────
export default function CumplimientoFiscal() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();

  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [selectedClienteId, setSelectedClienteId] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('trimestre');
  const [summaries, setSummaries] = useState<TaxSummary[]>([]);
  const [prevSummaries, setPrevSummaries] = useState<TaxSummary[]>([]);
  const [ytdSummaries, setYtdSummaries] = useState<TaxSummary[]>([]);
  const [ivaDetails, setIvaDetails] = useState<IvaDetail[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  // ── Load clients ───────────────────────────────────────
  const loadClientes = useCallback(async () => {
    if (!user?.id) return;
    setLoadingClientes(true);
    try {
      const { data: links } = await supabase
        .from('asesor_cliente')
        .select('cliente_id')
        .eq('asesor_id', user.id);

      const clienteIds = (links || []).map((l: { cliente_id: string }) => l.cliente_id);
      if (clienteIds.length === 0) {
        setClientes([]);
        setLoadingClientes(false);
        return;
      }

      const { data: clientesData } = await supabase
        .from('clientes')
        .select('id, razon_social, nombre_comercial')
        .in('id', clienteIds)
        .order('razon_social');

      setClientes((clientesData || []) as Cliente[]);
    } catch (err) {
      console.error('[Cumplimiento Fiscal] Error loading clients:', err);
    } finally {
      setLoadingClientes(false);
    }
  }, [user]);

  // ── Load tax data ──────────────────────────────────────
  const loadTaxData = useCallback(async (clienteId: string, filter: PeriodFilter) => {
    if (!clienteId) return;
    setLoadingData(true);
    try {
      const periods = getPeriodsForFilter(filter);
      const prevPeriods = getPreviousPeriods(filter);
      const currentYear = new Date().getFullYear();
      const currentPeriod = getCurrentPeriod();
      const ytdPeriods = getYearMonths(currentYear).filter(p => p <= currentPeriod);

      // Parallel fetch
      const [currentRes, prevRes, ytdRes] = await Promise.all([
        supabase.from('tax_summary').select('*').eq('cliente_id', clienteId).in('periodo', periods),
        supabase.from('tax_summary').select('*').eq('cliente_id', clienteId).in('periodo', prevPeriods),
        supabase.from('tax_summary').select('*').eq('cliente_id', clienteId).in('periodo', ytdPeriods),
      ]);

      const currentData = (currentRes.data || []) as TaxSummary[];
      const prevData = (prevRes.data || []) as TaxSummary[];
      const ytdData = (ytdRes.data || []) as TaxSummary[];

      // Fetch IVA detail for current summaries
      const summaryIds = currentData.map(s => s.id);
      let details: IvaDetail[] = [];
      if (summaryIds.length > 0) {
        const { data: detailData } = await supabase
          .from('tax_iva_detail')
          .select('*')
          .in('tax_summary_id', summaryIds);
        details = (detailData || []) as IvaDetail[];
      }

      setSummaries(currentData);
      setPrevSummaries(prevData);
      setYtdSummaries(ytdData);
      setIvaDetails(details);

      // Latest sync timestamp
      const latestSync = currentData
        .filter(s => s.last_sync_at)
        .sort((a, b) => (b.last_sync_at || '').localeCompare(a.last_sync_at || ''))
        [0]?.last_sync_at || null;
      setLastSyncAt(latestSync);
    } catch (err) {
      console.error('[Cumplimiento Fiscal] Error loading tax data:', err);
    } finally {
      setLoadingData(false);
    }
  }, []);

  // ── Effects ────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user?.rol === 'asesor') {
      loadClientes();
    }
  }, [user, authLoading, router, loadClientes]);

  useEffect(() => {
    if (selectedClienteId) {
      loadTaxData(selectedClienteId, periodFilter);
    } else {
      setSummaries([]);
      setPrevSummaries([]);
      setYtdSummaries([]);
      setIvaDetails([]);
      setLastSyncAt(null);
    }
  }, [selectedClienteId, periodFilter, loadTaxData]);

  // ── Computed: totals ───────────────────────────────────
  const totals = useMemo(() => {
    return summaries.reduce(
      (acc, s) => ({
        ivaRepercutido: acc.ivaRepercutido + num(s.iva_repercutido),
        ivaSoportado: acc.ivaSoportado + num(s.iva_soportado),
        ivaResultado: acc.ivaResultado + num(s.iva_resultado),
        retencionesPracticadas: acc.retencionesPracticadas + num(s.retenciones_practicadas),
        retencionesSoportadas: acc.retencionesSoportadas + num(s.retenciones_soportadas),
        totalSuplidos: acc.totalSuplidos + num(s.total_suplidos),
        baseEmitidas: acc.baseEmitidas + num(s.base_imponible_emitidas),
        baseRecibidas: acc.baseRecibidas + num(s.base_imponible_recibidas),
        numEmitidas: acc.numEmitidas + num(s.num_facturas_emitidas),
        numRecibidas: acc.numRecibidas + num(s.num_facturas_recibidas),
      }),
      {
        ivaRepercutido: 0, ivaSoportado: 0, ivaResultado: 0,
        retencionesPracticadas: 0, retencionesSoportadas: 0, totalSuplidos: 0,
        baseEmitidas: 0, baseRecibidas: 0, numEmitidas: 0, numRecibidas: 0,
      }
    );
  }, [summaries]);

  // ── Computed: YTD retenciones ──────────────────────────
  const ytdRetenciones = useMemo(() => {
    return ytdSummaries.reduce((sum, s) => sum + num(s.retenciones_practicadas), 0);
  }, [ytdSummaries]);

  // ── Computed: previous totals & comparison ─────────────
  const comparison = useMemo(() => {
    const prev = prevSummaries.reduce(
      (acc, s) => ({
        ivaRepercutido: acc.ivaRepercutido + num(s.iva_repercutido),
        ivaSoportado: acc.ivaSoportado + num(s.iva_soportado),
        ivaResultado: acc.ivaResultado + num(s.iva_resultado),
        baseEmitidas: acc.baseEmitidas + num(s.base_imponible_emitidas),
      }),
      { ivaRepercutido: 0, ivaSoportado: 0, ivaResultado: 0, baseEmitidas: 0 }
    );
    return {
      repercutido: pctChange(totals.ivaRepercutido, prev.ivaRepercutido),
      soportado: pctChange(totals.ivaSoportado, prev.ivaSoportado),
      resultado: pctChange(totals.ivaResultado, prev.ivaResultado),
      baseEmitidas: pctChange(totals.baseEmitidas, prev.baseEmitidas),
      prev,
    };
  }, [totals, prevSummaries]);

  // ── Computed: aggregated IVA breakdown ─────────────────
  const aggregatedIva = useMemo(() => {
    const map = new Map<string, { tipo: string; pct: number; base: number; cuota: number; count: number }>();
    ivaDetails.forEach(d => {
      const key = `${d.tipo_factura}-${d.porcentaje_iva}`;
      const existing = map.get(key);
      if (existing) {
        existing.base += num(d.base_imponible);
        existing.cuota += num(d.cuota_iva);
        existing.count += num(d.num_facturas);
      } else {
        map.set(key, {
          tipo: d.tipo_factura,
          pct: num(d.porcentaje_iva),
          base: num(d.base_imponible),
          cuota: num(d.cuota_iva),
          count: num(d.num_facturas),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.tipo !== b.tipo) return a.tipo === 'emitida' ? -1 : 1;
      return b.pct - a.pct;
    });
  }, [ivaDetails]);

  // ── Computed: monthly chart data ───────────────────────
  const monthlyChartData = useMemo(() => {
    const periods = getPeriodsForFilter(periodFilter);
    const summaryMap = new Map(summaries.map(s => [s.periodo, num(s.iva_resultado)]));
    return periods.map(p => ({
      month: p,
      value: summaryMap.get(p) || 0,
      hasData: summaryMap.has(p),
    }));
  }, [summaries, periodFilter]);

  // ── Computed: anomalies ────────────────────────────────
  const anomalies = useMemo(() => {
    const flags: Anomaly[] = [];
    if (summaries.length === 0) return flags;

    // 1. Negative IVA resultado → devolución
    if (totals.ivaResultado < 0) {
      flags.push({
        type: 'warning',
        icon: 'info',
        message: `Resultado IVA negativo (${formatEur(totals.ivaResultado)}). Implica solicitud de devolución ante la AEAT.`,
      });
    }

    // 2. Month-over-month change > 25% in IVA resultado
    const sorted = [...summaries].sort((a, b) => a.periodo.localeCompare(b.periodo));
    for (let i = 1; i < sorted.length; i++) {
      const prevVal = num(sorted[i - 1].iva_resultado);
      const currVal = num(sorted[i].iva_resultado);
      if (prevVal !== 0) {
        const change = Math.abs((currVal - prevVal) / Math.abs(prevVal)) * 100;
        if (change > 25) {
          flags.push({
            type: 'warning',
            icon: 'trending_up',
            message: `Variación mensual significativa en ${formatMonth(sorted[i].periodo)}: ${change.toFixed(1)}% en IVA resultado respecto al mes anterior.`,
          });
          break;
        }
      }
    }

    // 3. Base imponible emitidas drops > 20% vs previous period
    if (comparison.baseEmitidas !== null && comparison.baseEmitidas < -20) {
      flags.push({
        type: 'danger',
        icon: 'warning',
        message: `Base imponible emitidas ha caído un ${Math.abs(comparison.baseEmitidas).toFixed(1)}% respecto al periodo anterior. Revisar facturación.`,
      });
    }

    return flags;
  }, [summaries, totals, comparison]);

  // ── Loading skeleton ───────────────────────────────────
  if (authLoading || loadingClientes) {
    return (
      <Layout rol="asesor">
        <Head><title>Cumplimiento Fiscal - Externaliza2</title></Head>
        <div className="px-12 pb-12 space-y-10 pt-8">
          <Skeleton className="h-14 w-80 rounded-huge" />
          <Skeleton className="h-5 w-96 rounded-huge" />
          <div className="flex gap-4">
            <Skeleton className="h-11 w-72 rounded-2xl" />
            <Skeleton className="h-11 w-96 rounded-2xl ml-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-36 rounded-huge" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-72 rounded-huge" />
            <Skeleton className="h-72 rounded-huge" />
          </div>
          <Skeleton className="h-32 rounded-huge" />
        </div>
      </Layout>
    );
  }

  // ── Inline SVG chart component ─────────────────────────
  const renderMonthlyChart = () => {
    const data = monthlyChartData.map(d => ({
      mes: SHORT_MONTHS[parseInt(d.month.split('-')[1], 10) - 1],
      value: d.value,
      hasData: d.hasData,
    }));

    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-sm text-calm-muted font-light">
          Sin datos para el periodo seleccionado
        </div>
      );
    }

    const fmtAxis = (v: number) => {
      if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M€`;
      if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k€`;
      return `${v}€`;
    };

    const ChartTooltip = ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      const val = payload[0].value;
      return (
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-xl text-xs min-w-[150px]">
          <p className="font-semibold text-calm-text mb-1">{label}</p>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: val >= 0 ? '#1babe4' : '#f59e0b' }} />
            <span className="text-calm-muted">Resultado IVA:</span>
            <span className={`font-semibold ml-auto pl-2 ${val >= 0 ? 'text-sky-600' : 'text-amber-600'}`}>
              {formatEur(val)}
            </span>
          </div>
          <p className="text-calm-muted mt-1.5 text-[10px]">
            {val >= 0 ? 'IVA a ingresar' : 'IVA a devolver'}
          </p>
        </div>
      );
    };

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 12, right: 4, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tickFormatter={fmtAxis}
            tick={{ fontSize: 11, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip content={<ChartTooltip />} />
          <ReferenceLine y={0} stroke="#E2E8F0" strokeWidth={1.5} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={!d.hasData ? '#F1F5F9' : d.value >= 0 ? '#1babe4' : '#f59e0b'}
                fillOpacity={d.hasData ? 0.85 : 0.4}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // ── Render ─────────────────────────────────────────────
  return (
    <>
      <Head>
        <title>Cumplimiento Fiscal - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-16 space-y-10 pt-8 scrollbar-thin">

          {/* ─── 1. Header ─────────────────────────────── */}
          <div className="flex justify-between items-end animate-fade-in">
            <div>
              <h1 className="page-title">Cumplimiento Fiscal</h1>
              <p className="page-subtitle">
                {getPeriodLabel(periodFilter)} · IVA, retenciones y obligaciones tributarias
              </p>
            </div>
            {lastSyncAt && (
              <span className="badge badge-info flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-soft" />
                <Icon name="sync" className="text-[14px]" />
                Sincronizado {formatRelativeTime(lastSyncAt)}
              </span>
            )}
          </div>

          {/* ─── 2. Client selector + 3. Period filter ─── */}
          <div className="flex items-center gap-6 flex-wrap animate-slide-up stagger-1">
            <div className="flex items-center gap-3">
              <Icon name="person" className="text-[20px] text-calm-muted" />
              <select
                className="select min-w-[280px]"
                value={selectedClienteId}
                onChange={(e) => setSelectedClienteId(e.target.value)}
              >
                <option value="">Seleccionar cliente…</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nombre_comercial || c.razon_social}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-1 bg-slate-100/80 rounded-2xl p-1 ml-auto">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={cn(
                    'px-4 py-2 rounded-xl text-sm font-light transition-all duration-200',
                    periodFilter === opt.value
                      ? 'bg-white text-fresh-sky-600 shadow-sm font-medium'
                      : 'text-calm-muted hover:text-calm-text'
                  )}
                  onClick={() => setPeriodFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Data source info banner ──────────────── */}
          <div className="flex items-center gap-2.5 bg-slate-50/80 border border-calm-border rounded-2xl px-4 py-2.5 animate-slide-up stagger-2">
            <Icon name="info" className="text-[16px] text-fresh-sky-500 shrink-0" />
            <span className="text-xs font-light text-calm-muted leading-relaxed">
              Datos generados automaticamente a partir de las facturas contabilizadas. Fuente: Sistema de contabilizacion Externaliza2 → Cegid.
            </span>
          </div>

          {/* ─── No client selected ────────────────────── */}
          {!selectedClienteId && (
            <div className="flex flex-col items-center justify-center py-24 animate-fade-in">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-fresh-sky-50 to-steel-azure-50 flex items-center justify-center mb-6">
                <Icon name="account_balance" className="text-[40px] text-fresh-sky-400" />
              </div>
              <p className="text-lg font-light text-calm-text font-display mb-2">
                Selecciona un cliente
              </p>
              <p className="text-sm text-calm-muted font-light max-w-md text-center">
                Elige un cliente del desplegable superior para visualizar su situación fiscal, IVA y retenciones.
              </p>
            </div>
          )}

          {/* ─── Data loading skeleton ─────────────────── */}
          {selectedClienteId && loadingData && (
            <div className="space-y-8 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-36 rounded-huge" />
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Skeleton className="h-72 rounded-huge" />
                <Skeleton className="h-72 rounded-huge" />
              </div>
              <Skeleton className="h-28 rounded-huge" />
            </div>
          )}

          {/* ─── No data for client ────────────────────── */}
          {selectedClienteId && !loadingData && summaries.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
              <div className="w-20 h-20 rounded-3xl bg-slate-50 flex items-center justify-center mb-6">
                <Icon name="search_off" className="text-[40px] text-slate-300" />
              </div>
              <p className="text-lg font-light text-calm-text font-display mb-2">
                Sin datos fiscales
              </p>
              <p className="text-sm text-calm-muted font-light max-w-md text-center">
                No hay información de IVA para este cliente en el periodo seleccionado.
                Los datos se generan automáticamente al contabilizar facturas.
              </p>
            </div>
          )}

          {/* ─── Dashboard content ─────────────────────── */}
          {selectedClienteId && !loadingData && summaries.length > 0 && (
            <>
              {/* ─── Active period badge ─────────────────── */}
              <div className="flex items-center gap-4 animate-slide-up">
                {periodFilter === 'trimestre' && (
                  <div className="flex items-center gap-3 bg-gradient-to-r from-fresh-sky-500 to-steel-azure-500 rounded-2xl px-5 py-3 shadow-sm">
                    <span className="text-2xl font-display font-semibold text-white tracking-tight leading-none">
                      T{Math.floor(new Date().getMonth() / 3) + 1}
                    </span>
                    <div className="w-px h-8 bg-white/30" />
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-white/70 font-light">Periodo activo</span>
                      <span className="text-sm font-medium text-white">{getPeriodLabel(periodFilter)}</span>
                    </div>
                  </div>
                )}
                {periodFilter === 'mes' && (
                  <div className="flex items-center gap-3 bg-gradient-to-r from-fresh-sky-500 to-steel-azure-500 rounded-2xl px-5 py-3 shadow-sm">
                    <Icon name="calendar_today" className="text-[22px] text-white" />
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-white/70 font-light">Periodo activo</span>
                      <span className="text-sm font-medium text-white">{getPeriodLabel(periodFilter)}</span>
                    </div>
                  </div>
                )}
                {(periodFilter === 'year2025' || periodFilter === 'year2026') && (
                  <div className="flex items-center gap-3 bg-gradient-to-r from-steel-azure-500 to-fresh-sky-500 rounded-2xl px-5 py-3 shadow-sm">
                    <Icon name="date_range" className="text-[22px] text-white" />
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-white/70 font-light">Periodo activo</span>
                      <span className="text-sm font-medium text-white">{getPeriodLabel(periodFilter)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ─── 4. KPI Cards ────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* IVA Repercutido */}
                <div className="bg-gradient-to-br from-fresh-sky-50/60 to-white border border-fresh-sky-200/40 rounded-huge p-7 animate-slide-up stagger-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-fresh-sky-100 flex items-center justify-center">
                      <Icon name="receipt_long" className="text-[18px] text-fresh-sky-600" />
                    </div>
                    <span className="stat-label !mt-0">IVA Repercutido</span>
                  </div>
                  <p className="text-2xl font-light text-fresh-sky-700 font-display tabular-nums">
                    {formatEur(totals.ivaRepercutido)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    {totals.numEmitidas} facturas emitidas
                  </p>
                </div>

                {/* IVA Soportado */}
                <div className="bg-gradient-to-br from-slate-50/60 to-white border border-slate-200/40 rounded-huge p-7 animate-slide-up stagger-2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Icon name="shopping_cart" className="text-[18px] text-slate-500" />
                    </div>
                    <span className="stat-label !mt-0">IVA Soportado</span>
                  </div>
                  <p className="text-2xl font-light text-slate-600 font-display tabular-nums">
                    {formatEur(totals.ivaSoportado)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    {totals.numRecibidas} facturas recibidas
                  </p>
                </div>

                {/* Resultado IVA */}
                <div className={cn(
                  'rounded-huge p-7 animate-slide-up stagger-3 border',
                  totals.ivaResultado >= 0
                    ? 'bg-gradient-to-br from-red-50/40 to-white border-red-200/40'
                    : 'bg-gradient-to-br from-emerald-50/40 to-white border-emerald-200/40'
                )}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      'w-9 h-9 rounded-xl flex items-center justify-center',
                      totals.ivaResultado >= 0 ? 'bg-red-100' : 'bg-emerald-100'
                    )}>
                      <Icon
                        name="balance"
                        className={cn('text-[18px]', totals.ivaResultado >= 0 ? 'text-red-500' : 'text-emerald-600')}
                      />
                    </div>
                    <span className="stat-label !mt-0">Resultado IVA</span>
                  </div>
                  <p className={cn(
                    'text-2xl font-light font-display tabular-nums',
                    totals.ivaResultado >= 0 ? 'text-red-600' : 'text-emerald-700'
                  )}>
                    {formatEur(totals.ivaResultado)}
                  </p>
                  <p className={cn(
                    'text-[10px] uppercase tracking-widest mt-2',
                    totals.ivaResultado >= 0 ? 'text-red-400' : 'text-emerald-500'
                  )}>
                    {totals.ivaResultado >= 0 ? 'A ingresar (deuda)' : 'A devolver (favorable)'}
                  </p>
                </div>

                {/* Retenciones Acumuladas YTD */}
                <div className="bg-gradient-to-br from-steel-azure-50/40 to-white border border-steel-azure-200/40 rounded-huge p-7 animate-slide-up stagger-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-steel-azure-100 flex items-center justify-center">
                      <Icon name="savings" className="text-[18px] text-steel-azure-600" />
                    </div>
                    <span className="stat-label !mt-0">Retenciones Acumuladas</span>
                  </div>
                  <p className="text-2xl font-light text-steel-azure-700 font-display tabular-nums">
                    {formatEur(ytdRetenciones)}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-calm-muted mt-2">
                    Acumulado año fiscal {new Date().getFullYear()}
                  </p>
                </div>
              </div>

              {/* ─── 5. IVA Table + 6. Monthly Chart ─────── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* IVA Breakdown Table */}
                <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 animate-slide-up stagger-3">
                  <h2 className="section-title mb-6">Desglose IVA por Tipo</h2>
                  {aggregatedIva.length === 0 ? (
                    <div className="text-center py-10">
                      <Icon name="table_chart" className="text-[32px] text-slate-300 mb-3" />
                      <p className="text-sm text-calm-muted font-light">Sin desglose disponible</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-2">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-calm-border">
                            <th className="text-left py-3 px-2 stat-label !mt-0 !text-[9px]">Tipo</th>
                            <th className="text-right py-3 px-2 stat-label !mt-0 !text-[9px]">% IVA</th>
                            <th className="text-right py-3 px-2 stat-label !mt-0 !text-[9px]">Base Imponible</th>
                            <th className="text-right py-3 px-2 stat-label !mt-0 !text-[9px]">Cuota IVA</th>
                            <th className="text-right py-3 px-2 stat-label !mt-0 !text-[9px]">Nº Fact.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aggregatedIva.map((row, idx) => (
                            <tr
                              key={`${row.tipo}-${row.pct}`}
                              className={cn(
                                'transition-colors hover:bg-fresh-sky-50/30',
                                idx < aggregatedIva.length - 1 && 'border-b border-calm-border/50'
                              )}
                            >
                              <td className="py-3 px-2">
                                <span className={cn(
                                  'inline-flex items-center gap-1.5 text-xs font-light',
                                  row.tipo === 'emitida' ? 'text-fresh-sky-700' : 'text-slate-600'
                                )}>
                                  <span className={cn(
                                    'w-1.5 h-1.5 rounded-full',
                                    row.tipo === 'emitida' ? 'bg-fresh-sky-400' : 'bg-slate-400'
                                  )} />
                                  {row.tipo === 'emitida' ? 'Emitida' : 'Recibida'}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-right font-light text-calm-text tabular-nums">
                                {row.pct}%
                              </td>
                              <td className="py-3 px-2 text-right font-light text-calm-text tabular-nums">
                                {formatEur(row.base)}
                              </td>
                              <td className="py-3 px-2 text-right font-light text-calm-text tabular-nums">
                                {formatEur(row.cuota)}
                              </td>
                              <td className="py-3 px-2 text-right font-light text-calm-muted tabular-nums">
                                {row.count}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-calm-border">
                            <td className="py-3 px-2 text-xs font-medium text-calm-text" colSpan={2}>Total</td>
                            <td className="py-3 px-2 text-right text-xs font-medium text-calm-text tabular-nums">
                              {formatEur(aggregatedIva.reduce((s, r) => s + r.base, 0))}
                            </td>
                            <td className="py-3 px-2 text-right text-xs font-medium text-calm-text tabular-nums">
                              {formatEur(aggregatedIva.reduce((s, r) => s + r.cuota, 0))}
                            </td>
                            <td className="py-3 px-2 text-right text-xs font-medium text-calm-muted tabular-nums">
                              {aggregatedIva.reduce((s, r) => s + r.count, 0)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Monthly Evolution Chart */}
                <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 animate-slide-up stagger-4">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="section-title">Evolución Mensual IVA</h2>
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-calm-muted">
                        <span className="w-2.5 h-2.5 rounded-sm bg-fresh-sky-400 opacity-85" />
                        Deuda
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-calm-muted">
                        <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 opacity-85" />
                        Devolución
                      </span>
                    </div>
                  </div>
                  {renderMonthlyChart()}
                </div>
              </div>

              {/* ─── 7. Comparison vs Previous Period ────── */}
              {prevSummaries.length > 0 && (
                <div className="bg-gradient-to-br from-white to-slate-50/40 border border-calm-border rounded-huge p-8 animate-slide-up stagger-5">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="compare_arrows" className="text-[20px] text-calm-muted" />
                    <h2 className="section-title">Comparativa</h2>
                    <span className="text-sm text-calm-muted font-light ml-1">
                      {getComparisonLabel(periodFilter)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Repercutido comparison */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-calm-border/50">
                      <div>
                        <p className="stat-label !mt-0">IVA Repercutido</p>
                        <p className="text-lg font-light text-calm-text font-display tabular-nums mt-1">
                          {formatEur(totals.ivaRepercutido)}
                        </p>
                      </div>
                      <div className="text-right">
                        {comparison.repercutido !== null ? (
                          <span className={cn(
                            'inline-flex items-center gap-1 text-sm font-light',
                            comparison.repercutido > 0 ? 'text-emerald-600' : comparison.repercutido < 0 ? 'text-red-500' : 'text-calm-muted'
                          )}>
                            <Icon
                              name={comparison.repercutido > 0 ? 'arrow_upward' : comparison.repercutido < 0 ? 'arrow_downward' : 'remove'}
                              className="text-[16px]"
                            />
                            {formatPct(comparison.repercutido)}
                          </span>
                        ) : (
                          <span className="text-xs text-calm-accent">Sin ref.</span>
                        )}
                      </div>
                    </div>

                    {/* Soportado comparison */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-calm-border/50">
                      <div>
                        <p className="stat-label !mt-0">IVA Soportado</p>
                        <p className="text-lg font-light text-calm-text font-display tabular-nums mt-1">
                          {formatEur(totals.ivaSoportado)}
                        </p>
                      </div>
                      <div className="text-right">
                        {comparison.soportado !== null ? (
                          <span className={cn(
                            'inline-flex items-center gap-1 text-sm font-light',
                            comparison.soportado > 0 ? 'text-red-500' : comparison.soportado < 0 ? 'text-emerald-600' : 'text-calm-muted'
                          )}>
                            <Icon
                              name={comparison.soportado > 0 ? 'arrow_upward' : comparison.soportado < 0 ? 'arrow_downward' : 'remove'}
                              className="text-[16px]"
                            />
                            {formatPct(comparison.soportado)}
                          </span>
                        ) : (
                          <span className="text-xs text-calm-accent">Sin ref.</span>
                        )}
                      </div>
                    </div>

                    {/* Resultado comparison */}
                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-calm-border/50">
                      <div>
                        <p className="stat-label !mt-0">Resultado IVA</p>
                        <p className={cn(
                          'text-lg font-light font-display tabular-nums mt-1',
                          totals.ivaResultado >= 0 ? 'text-red-600' : 'text-emerald-700'
                        )}>
                          {formatEur(totals.ivaResultado)}
                        </p>
                      </div>
                      <div className="text-right">
                        {comparison.resultado !== null ? (
                          <span className={cn(
                            'inline-flex items-center gap-1 text-sm font-light text-calm-muted'
                          )}>
                            <Icon
                              name={comparison.resultado > 0 ? 'arrow_upward' : comparison.resultado < 0 ? 'arrow_downward' : 'remove'}
                              className="text-[16px]"
                            />
                            {formatPct(comparison.resultado)}
                          </span>
                        ) : (
                          <span className="text-xs text-calm-accent">Sin ref.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ─── 8. Anomaly Flags ────────────────────── */}
              {anomalies.length > 0 && (
                <div className="space-y-3 animate-slide-up stagger-5">
                  {anomalies.map((a, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex items-start gap-4 p-5 rounded-2xl border',
                        a.type === 'warning'
                          ? 'bg-amber-50/60 border-amber-200/60'
                          : 'bg-orange-50/60 border-orange-200/60'
                      )}
                    >
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5',
                        a.type === 'warning' ? 'bg-amber-100' : 'bg-orange-100'
                      )}>
                        <Icon
                          name={a.icon}
                          className={cn(
                            'text-[18px]',
                            a.type === 'warning' ? 'text-amber-600' : 'text-orange-600'
                          )}
                        />
                      </div>
                      <div>
                        <p className={cn(
                          'text-sm font-medium mb-0.5',
                          a.type === 'warning' ? 'text-amber-800' : 'text-orange-800'
                        )}>
                          {a.type === 'danger' ? 'Alerta' : 'Aviso'}
                        </p>
                        <p className={cn(
                          'text-sm font-light',
                          a.type === 'warning' ? 'text-amber-700' : 'text-orange-700'
                        )}>
                          {a.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ─── 9. Suplidos ─────────────────────────── */}
              <div className="bg-gradient-to-br from-white to-slate-50/30 border border-calm-border rounded-huge p-7 animate-slide-up stagger-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Icon name="receipt" className="text-[20px] text-slate-500" />
                    </div>
                    <div>
                      <p className="stat-label !mt-0">Suplidos (No sujetos a IVA)</p>
                      <p className="text-xs text-calm-muted font-light mt-0.5">
                        Gastos por cuenta del cliente no sujetos a tributación
                      </p>
                    </div>
                  </div>
                  <p className="text-2xl font-light text-calm-text font-display tabular-nums">
                    {formatEur(totals.totalSuplidos)}
                  </p>
                </div>
              </div>

              {/* ─── 10. Last sync timestamp ─────────────── */}
              {lastSyncAt && (
                <div className="flex items-center justify-end gap-2 pt-2 pb-4 animate-fade-in">
                  <Icon name="update" className="text-[14px] text-calm-accent" />
                  <p className="text-[11px] text-calm-accent font-light">
                    Última sincronización: {new Date(lastSyncAt).toLocaleString('es-ES', {
                      day: 'numeric', month: 'long', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Layout>
    </>
  );
}
